import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import useAuthUser from "../components/common/useAuthUser";

const WishContext = createContext();

export const WishProvider = ({ children }) => {
  const { authUser, isLoading } = useAuthUser();
  const [wish, setWish] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  const storageKey = useMemo(
    () => (authUser ? `wish_${authUser._id}` : "wish_guest"),
    [authUser]
  );

  /* ---------- HYDRATE ONCE ---------- */
  useEffect(() => {
    if (isLoading || hydrated) return;

    const stored = localStorage.getItem(storageKey);
    setWish(stored ? JSON.parse(stored) : []);
    setHydrated(true);
  }, [storageKey, isLoading, hydrated]);

  /* ---------- PERSIST ---------- */
  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(storageKey, JSON.stringify(wish));
  }, [wish, storageKey, hydrated]);

  /* ---------- MERGE GUEST WISH ON LOGIN ---------- */
  useEffect(() => {
    if (!authUser || !hydrated) return;

    const guest = JSON.parse(localStorage.getItem("wish_guest") || "[]");
    if (!guest.length) return;

    setWish((prev) => {
      const map = new Map();

      prev.forEach((item) => map.set(item._id, { ...item }));
      guest.forEach((item) => {
        if (map.has(item._id)) {
          map.set(item._id, {
            ...map.get(item._id),
            wishQuantity:
              map.get(item._id).wishQuantity + item.wishQuantity,
          });
        } else {
          map.set(item._id, item);
        }
      });

      return Array.from(map.values());
    });

    localStorage.removeItem("wish_guest");
  }, [authUser, hydrated]);

  /* ---------- ACTIONS ---------- */

  const addToWish = (item) => {
    setWish((prev) => {
      const found = prev.find((p) => p?._id === item?._id);

      if (!found) {
        // toast.success("Added to Wishlist ❤️");
        return [...prev, { ...item, wishQuantity: 1 }];
      }

      if (found.wishQuantity >= item.quantity) return prev;

      return prev.map((p) =>
        p._id === item._id
          ? { ...p, wishQuantity: p.wishQuantity + 1 }
          : p
      );
    });
  };

  const updateQuantity = (_id, qty) =>
    setWish((prev) =>
      prev.map((i) =>
        i._id === _id
          ? { ...i, wishQuantity: Math.max(1, Math.min(qty, i.quantity)) }
          : i
      )
    );
  const removeFromWish = (_id) =>
    setWish((prev) => prev.filter((i) => i._id !== _id));

  const clearWish = () => {
    setWish([]);
    localStorage.removeItem(storageKey);
  };

  return (
    <WishContext.Provider
      value={{ wish, addToWish, updateQuantity, removeFromWish, clearWish }}
    >
      {children}
    </WishContext.Provider>
  );
};

export const useWish = () => useContext(WishContext);
